import { useCallback, useEffect, useRef, useState } from 'react';
import { cognitionApi } from '../services/cognitionApi';
import { GSCORE_HISTORY_STUB } from '../fixtures/gscoreStub';

// Backend V1 stub is live for GET /api/v1/cognition/history.
const USE_STUB = false;

export function useGScoreHistory({ enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Only the most recent request may write state. This hook instance outlives
  // the modal that drives it (ScoreHistoryModal is always rendered by
  // GScorePage; only `enabled` toggles), so a close-and-reopen issues a second
  // request while the first is still in flight. Without this, a stalled first
  // request that later times out replaced a correctly rendered chart with an
  // error.
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) return;
    const requestId = ++requestIdRef.current;
    const current = () => requestId === requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      if (USE_STUB) {
        await new Promise((r) => setTimeout(r, 80));
        if (!current()) return;
        setData(GSCORE_HISTORY_STUB);
      } else {
        const res = await cognitionApi.fetchHistory();
        if (!current()) return;
        setData(res);
      }
    } catch (e) {
      if (!current()) return;
      // Same disclosure rule as the other cognition hooks: never surface a raw
      // backend message to the user.
      if (process.env.NODE_ENV !== 'production') {
        console.error('[useGScoreHistory] load error:', e);
      }
      setError('Something went wrong loading your history. Please try again.');
    } finally {
      if (current()) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    // Retiring the id on cleanup means a request left over from a previous
    // `enabled` window can never paint into the next one.
    return () => {
      requestIdRef.current += 1;
    };
  }, [enabled, load]);

  return { data, loading, error, reload: load };
}
